import { CityBounds, LatLng } from '../types/map';

// ─── IndexedDB Cache (no size limit unlike localStorage's 5MB) ───────────

const IDB_NAME = 'geo-sleeper-cache';
const IDB_STORE = 'maps';
const IDB_VERSION = 1;

function openCacheDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  try {
    const db = await openCacheDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return undefined;
  }
}

async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await openCacheDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    const req = store.put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** List all keys in the cache store */
export async function idbKeys(): Promise<string[]> {
  try {
    const db = await openCacheDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.getAllKeys();
      req.onsuccess = () => resolve(req.result as string[]);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
  'https://overpass.openstreetmap.ru/api/interpreter',
];
const NOMINATIM_API = 'https://nominatim.openstreetmap.org';

// Fallback bounding box size when Nominatim doesn't provide one
const FALLBACK_BBOX_SIZE_KM = 4;

// Maximum bbox dimension in km — caps very large cities to keep Overpass queries feasible
const MAX_BBOX_SIZE_KM = 4;

export interface GeocodingResult {
  name: string;
  displayName: string;
  lat: number;
  lng: number;
  bounds?: CityBounds; // from Nominatim's boundingbox field
}

export interface RawOSMData {
  streets: OSMElement[];
  buildings: OSMElement[];
  water: OSMElement[];
  transit: OSMElement[];
  pois: OSMElement[];
  landuse: OSMElement[];
  neighborhoods: OSMElement[];
}

export interface OSMElement {
  type: 'node' | 'way' | 'relation';
  id: number;
  lat?: number;
  lon?: number;
  tags?: Record<string, string>;
  nodes?: number[];
  members?: OSMRelationMember[];
  geometry?: { lat: number; lon: number }[];
}

interface OSMRelationMember {
  type: 'node' | 'way' | 'relation';
  ref: number;
  role: string;
  lat?: number;
  lon?: number;
  geometry?: { lat: number; lon: number }[];
}

export async function geocodeCity(query: string): Promise<GeocodingResult[]> {
  const params = new URLSearchParams({
    q: query,
    format: 'json',
    limit: '5',
    addressdetails: '1',
  });

  const response = await fetch(`${NOMINATIM_API}/search?${params}`, {
    headers: { 'User-Agent': 'GeoSleeper/0.1 (game prototype)' },
  });

  if (!response.ok) throw new Error(`Geocoding failed: ${response.status}`);

  const results = await response.json();
  return results
    .filter((r: any) => r.type === 'city' || r.type === 'town' || r.type === 'village' || r.class === 'place' || r.class === 'boundary')
    .map((r: any) => {
      // Nominatim returns boundingbox as [south, north, west, east] strings
      const bb = r.boundingbox;
      const bounds: CityBounds | undefined = bb ? {
        south: parseFloat(bb[0]),
        north: parseFloat(bb[1]),
        west: parseFloat(bb[2]),
        east: parseFloat(bb[3]),
      } : undefined;

      return {
        name: r.name || r.display_name.split(',')[0],
        displayName: r.display_name,
        lat: parseFloat(r.lat),
        lng: parseFloat(r.lon),
        bounds,
      };
    });
}

/**
 * Returns bounds for the city. Uses the real city bounds from Nominatim if available,
 * capped to MAX_BBOX_SIZE_KM. Falls back to a fixed-size box around center.
 */
export function computeBounds(center: LatLng, realBounds?: CityBounds, maxSizeKm?: number): CityBounds {
  if (realBounds) {
    // Measure the real bounds size
    const latSpanKm = (realBounds.north - realBounds.south) * 111;
    const lngSpanKm = (realBounds.east - realBounds.west) * 111 * Math.cos((center.lat * Math.PI) / 180);
    const maxSpanKm = Math.max(latSpanKm, lngSpanKm);

    const capKm = maxSizeKm ?? MAX_BBOX_SIZE_KM;
    if (maxSpanKm <= capKm) {
      // City fits within our cap — use real bounds as-is
      return realBounds;
    }

    // City is too large — shrink to cap centered on the center point
    const scale = capKm / maxSpanKm;
    const latDelta = ((realBounds.north - realBounds.south) * scale) / 2;
    const lngDelta = ((realBounds.east - realBounds.west) * scale) / 2;
    return {
      south: center.lat - latDelta,
      north: center.lat + latDelta,
      west: center.lng - lngDelta,
      east: center.lng + lngDelta,
    };
  }

  // Fallback: fixed-size box
  const fallbackKm = maxSizeKm ?? FALLBACK_BBOX_SIZE_KM;
  const latDelta = fallbackKm / 2 / 111;
  const lngDelta = fallbackKm / 2 / (111 * Math.cos((center.lat * Math.PI) / 180));
  return {
    south: center.lat - latDelta,
    north: center.lat + latDelta,
    west: center.lng - lngDelta,
    east: center.lng + lngDelta,
  };
}

function bbox(bounds: CityBounds): string {
  return `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`;
}

async function queryOverpass(query: string): Promise<OSMElement[]> {
  const maxRounds = 3;
  const TIMEOUT_MS = 120_000; // 2 min per attempt — Overpass queries can be slow

  for (let round = 0; round < maxRounds; round++) {
    for (let i = 0; i < OVERPASS_ENDPOINTS.length; i++) {
      const endpoint = OVERPASS_ENDPOINTS[i];

      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `data=${encodeURIComponent(query)}`,
          signal: controller.signal,
        });
        clearTimeout(timer);

        if (response.status === 429 || response.status === 504) {
          console.warn(`Overpass ${response.status} from ${endpoint}, trying next mirror...`);
          continue;
        }

        if (!response.ok) {
          throw new Error(`Overpass API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        return data.elements || [];
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isTimeout = msg.includes('abort');
        console.warn(`Overpass ${isTimeout ? 'timeout' : 'error'} from ${endpoint}: ${msg}`);
        continue;
      }
    }

    if (round < maxRounds - 1) {
      const delay = (round + 1) * 10_000;
      console.warn(`All Overpass mirrors failed round ${round + 1}, retrying in ${delay / 1000}s...`);
      await new Promise(r => setTimeout(r, delay));
    }
  }

  throw new Error('Overpass API: all mirrors failed after retries');
}

// Classify raw OSM elements by their tags into data layers
function sortElements(elements: OSMElement[]): Omit<RawOSMData, 'transit'> {
  const streets: OSMElement[] = [];
  const buildings: OSMElement[] = [];
  const water: OSMElement[] = [];
  const pois: OSMElement[] = [];
  const landuse: OSMElement[] = [];
  const neighborhoods: OSMElement[] = [];

  for (const el of elements) {
    const tags = el.tags || {};

    if (tags.highway) {
      streets.push(el);
    } else if (tags.tourism === 'attraction' || tags.tourism === 'museum' || tags.historic === 'monument' || tags.historic === 'memorial' || tags.historic === 'castle') {
      // Tourist attractions / historic landmarks take priority over building classification
      pois.push(el);
    } else if (tags.building) {
      buildings.push(el);
    } else if (tags.waterway || tags.natural === 'water' || tags.natural === 'coastline') {
      water.push(el);
    } else if (tags.place === 'neighbourhood' || tags.place === 'suburb' || (tags.place === 'quarter' && !tags.historic && tags.boundary !== 'historic')) {
      neighborhoods.push(el);
    } else if (tags.landuse || (tags.leisure && (tags.leisure === 'park' || tags.leisure === 'garden') && el.type === 'way' && el.geometry && el.geometry.length > 3)) {
      landuse.push(el);
    } else if (tags.route === 'subway' || tags.route === 'light_rail' || tags.route === 'tram' || tags.route === 'bus' || tags.route === 'ferry' || tags.railway || tags.station || tags.amenity === 'ferry_terminal') {
      // Transit — handled separately but catch strays
    } else if (tags.amenity || tags.shop || tags.tourism || tags.historic || tags.craft || tags.leisure) {
      pois.push(el);
    }
  }

  return { streets, buildings, water, pois, landuse, neighborhoods };
}

export async function extractCityData(
  bounds: CityBounds,
  onProgress?: (stage: string, progress: number) => void
): Promise<RawOSMData> {
  const b = bbox(bounds);

  onProgress?.('Extracting city data...', 0.1);

  // Build the query template (bbox is injected per tile)
  const buildQuery = (tileBbox: string) => `
    [out:json][timeout:120][bbox:${tileBbox}];
    (
      /* Streets — skip service/path/steps/cycleway to reduce data volume */
      way["highway"~"^(motorway|trunk|primary|secondary|tertiary|residential|unclassified|pedestrian|living_street)$"];
      /* Footways — only standalone paths, not sidewalks along roads */
      way["highway"="footway"]["footway"!="sidewalk"]["footway"!="crossing"];
      /* Buildings — all buildings for gameplay (Hide Inside, etc.) */
      way["building"];
      /* Water */
      way["waterway"~"^(river|canal|stream)$"];
      way["natural"="water"];
      relation["natural"="water"];
      way["natural"="coastline"];
      /* Transit */
      relation["route"="subway"];
      relation["route"="light_rail"];
      relation["route"="tram"];
      relation["route"="bus"];
      relation["route"="ferry"];
      node["railway"="station"][!"disused"][!"abandoned"](${b});
      node["railway"="halt"](${b});
      node["station"="subway"](${b});
      node["amenity"="ferry_terminal"](${b});
      /* POIs */
      node["amenity"~"^(pharmacy|hospital|clinic|restaurant|cafe|bar|marketplace|place_of_worship|bench|locker)$"];
      node["shop"~"^(hardware|doityourself|sports|outdoor|hunting|electronics|mobile_phone|military_surplus|weapons|supermarket)$"];
      node["tourism"~"^(attraction|hotel|hostel|museum)$"];
      way["tourism"~"^(attraction|museum)$"];
      relation["tourism"~"^(attraction|museum)$"];
      node["historic"~"^(monument|memorial|castle)$"];
      way["historic"~"^(monument|memorial|castle)$"];
      relation["historic"~"^(monument|memorial|castle)$"];
      node["leisure"="park"];
      node["craft"="locksmith"];
      /* Landuse */
      way["landuse"~"^(commercial|residential|industrial|retail|military|recreation_ground)$"];
      relation["landuse"~"^(commercial|residential|industrial|retail|military|recreation_ground)$"];
      way["leisure"~"^(park|garden)$"];
      /* Neighborhoods — prefer nodes (most common for neighborhoods), plus areas */
      node["place"~"^(neighbourhood|suburb|quarter)$"];
      relation["place"~"^(neighbourhood|suburb)$"];
      way["place"~"^(neighbourhood|suburb)$"];
    );
    out body geom;
  `;

  // Split bounds into 2×2 tile grid for more reliable downloading
  const midLat = (bounds.south + bounds.north) / 2;
  const midLng = (bounds.west + bounds.east) / 2;
  const tiles: CityBounds[] = [
    { south: bounds.south, north: midLat, west: bounds.west, east: midLng },
    { south: bounds.south, north: midLat, west: midLng, east: bounds.east },
    { south: midLat, north: bounds.north, west: bounds.west, east: midLng },
    { south: midLat, north: bounds.north, west: midLng, east: bounds.east },
  ];

  // IndexedDB cache: keyed on full bounds
  const cacheKey = `cache-${b}`;
  let allElements: OSMElement[];

  console.log(`[Cache] Looking for key: ${cacheKey}`);
  const cached = await idbGet<OSMElement[]>(cacheKey);
  if (cached) {
    console.log(`[Cache] HIT — loading from IndexedDB`);
    onProgress?.('Loading from cache...', 0.5);
    allElements = cached;
  } else {
    console.log(`[Cache] MISS — downloading from Overpass in ${tiles.length} tiles`);
    allElements = [];
    const seenIds = new Set<string>();

    for (let ti = 0; ti < tiles.length; ti++) {
      const tile = tiles[ti];
      const tileBbox = bbox(tile);
      const tileQuery = buildQuery(tileBbox);
      const progress = 0.1 + (ti / tiles.length) * 0.6;
      onProgress?.(`Downloading tile ${ti + 1}/${tiles.length}...`, progress);

      const tileElements = await queryOverpass(tileQuery);
      // Deduplicate: skip elements already seen from overlapping tiles
      for (const el of tileElements) {
        const key = `${el.type}-${el.id}`;
        if (!seenIds.has(key)) {
          seenIds.add(key);
          allElements.push(el);
        }
      }
      console.log(`[Tile ${ti + 1}/${tiles.length}] Got ${tileElements.length} elements (${allElements.length} total unique)`);
    }

    try {
      await idbSet(cacheKey, allElements);
      console.log(`[Cache] Saved to IndexedDB: ${cacheKey}`);
    } catch (e) {
      console.warn(`[Cache] Failed to save ${cacheKey}:`, e);
    }
  }

  onProgress?.('Processing features...', 0.8);
  const sorted = sortElements(allElements);

  // Extract transit from the combined results
  const transit: OSMElement[] = [];
  for (const el of allElements) {
    const tags = el.tags || {};
    if (tags.route === 'subway' || tags.route === 'light_rail' || tags.route === 'tram' ||
        tags.route === 'bus' || tags.route === 'ferry' ||
        tags.railway || tags.station || tags.amenity === 'ferry_terminal') {
      transit.push(el);
    }
  }

  onProgress?.('Data extraction complete', 1.0);

  return { ...sorted, transit };
}
